import Box from '@mui/material/Box'
import Skeleton from '@mui/material/Skeleton'
import { useEffect, useState } from 'react'
import { api } from '../api/client'

/**
 * Renders an image from an authenticated endpoint.
 *
 * <p>A plain {@code <img src>} cannot send the bearer token, so site photographs would
 * come back 401 and render as a broken icon. The bytes are fetched with the configured
 * axios client and handed to the browser as an object URL, which is also revoked on
 * unmount so a long gallery session does not leak blobs.</p>
 */
export function AuthedImage({
  src,
  alt,
  height,
  onClick,
  sx,
}: {
  src: string
  alt?: string
  height?: number | string
  onClick?: () => void
  sx?: object
}) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let revoked = false
    let created: string | null = null

    async function load() {
      try {
        const response = await api.get<Blob>(src.replace(/^\/api\/v1/, ''), {
          responseType: 'blob',
        })
        if (revoked) return
        created = URL.createObjectURL(response.data)
        setObjectUrl(created)
      } catch {
        if (!revoked) setFailed(true)
      }
    }
    void load()

    return () => {
      revoked = true
      if (created) URL.revokeObjectURL(created)
    }
  }, [src])

  if (failed) {
    return (
      <Box
        sx={{
          height: height ?? 150,
          display: 'grid',
          placeItems: 'center',
          bgcolor: '#eef2f5',
          color: 'text.secondary',
          fontSize: 13,
          ...sx,
        }}
      >
        Image unavailable
      </Box>
    )
  }

  if (!objectUrl) {
    return <Skeleton variant="rectangular" height={height ?? 150} sx={sx} />
  }

  return (
    <Box
      component="img"
      src={objectUrl}
      alt={alt}
      onClick={onClick}
      sx={{
        display: 'block',
        width: '100%',
        height: height ?? 150,
        objectFit: 'cover',
        bgcolor: '#eef2f5',
        cursor: onClick ? 'pointer' : 'default',
        ...sx,
      }}
    />
  )
}
